import { Divider, Modal, Tooltip } from '@byecode/ui'
import type { BreakPoint } from '@lighthouse/core'
import { useAtomAction, useAtomData } from '@lighthouse/shared'
import * as React from 'react'
import { useCallback } from 'react'
import { useImmer } from 'use-immer'

import { createBreakPointAtom, deleteBreakPointAtom, setCurrentBreakPointIdAtom, updateBreakPointAtom } from '@/atoms/application/action'
import { breakPointListAtom } from '@/atoms/application/state'
import { useCurrentBreakPointId, useMainBreakPoint } from '@/hooks/useApplication'

import * as CM from '../styles'
import { BreakPointForm } from './BreakPointForm'
import { PreviewPopover } from './PreviewPopover'
import * as SC from './styles'

interface PreviewIconProps {
    hoverColor?: string
}

interface PreviewIconState {
    open: boolean
    data?: BreakPoint
}
interface BreakPointDescriptionProps {
    title: string
    description?: string
}

const BreakPointDescription: React.FC<BreakPointDescriptionProps> = ({ title, description }) => {
    return (
        <SC.Box>
            <SC.Title>{title}</SC.Title>
            {description && <SC.Description>{description}</SC.Description>}
        </SC.Box>
    )
}

export const PreviewIcon: React.FunctionComponent<PreviewIconProps> = ({ hoverColor }) => {
    const [state, setState] = useImmer<PreviewIconState>({
        open: false
    })
    const breakPointId = useCurrentBreakPointId()
    const mainBreakPoint = useMainBreakPoint()
    const { run: setBreakPointId } = useAtomAction(setCurrentBreakPointIdAtom)
    const breakPointList = useAtomData(breakPointListAtom)
    const { run: createBreakPoint, loading: createLoading } = useAtomAction(createBreakPointAtom)
    const { run: updateBreakPoint, loading: updateLoading } = useAtomAction(updateBreakPointAtom)
    const { run: deleteBreakPoint } = useAtomAction(deleteBreakPointAtom)
    const { open, data } = state

    const handleChange = useCallback(
        (val: string) => {
            setBreakPointId(val)
        },
        [setBreakPointId]
    )

    const handleOpenCreate = useCallback(() => {
        setState({
            open: true
        })
    }, [setState])

    const handleOpenUpdate = useCallback(
        (val: BreakPoint) => {
            setState({
                open: true,
                data: val
            })
        },
        [setState]
    )

    const handleCreate = useCallback(
        async (val: BreakPoint) => {
            const isCreate = await createBreakPoint(val)
            if (isCreate) {
                setState({
                    open: false
                })
            }
        },
        [createBreakPoint, setState]
    )

    const handleUpdate = useCallback(
        async (val: BreakPoint) => {
            const isUpdate = await updateBreakPoint(val)
            if (isUpdate) {
                setState({
                    open: false
                })
            }
        },
        [setState, updateBreakPoint]
    )

    const handleClose = useCallback(() => {
        setState({
            open: false
        })
    }, [setState])

    const handleDelete = useCallback(
        async (val: BreakPoint) => {
            const isConfirm = await Modal.confirm({
                title: '确认删除',
                content: `确认删除「${val.name}」断点？删除后不可恢复，请谨慎操作。`
            })
            if (isConfirm) {
                deleteBreakPoint(val.id)
            }
        },
        [deleteBreakPoint]
    )

    return (
        <SC.PreviewContainer>
            <SC.Wrapper>
                {breakPointList.map(item => {
                    const description = item.id === 'desktop' ? `${item.name}(${item.width})：✱主断点` : `${item.name}(${item.width})`
                    return (
                        <Tooltip
                            key={item.id}
                            styles={{ tooltip: { padding: '0px!important' } }}
                            placement="bottom"
                            title={<BreakPointDescription title={description} />}
                        >
                            <SC.IconWrapper
                                className={breakPointId === item.id ? 'active' : undefined}
                                onClick={() => {
                                    setBreakPointId(item.id)
                                }}
                                hoverColor={hoverColor}
                            >
                                <CM.Icon
                                    fill={breakPointId === item.id ? 'var(--color-black)' : 'var(--color-gray-500)'}
                                    type={item.icon}
                                    size={18}
                                />
                                {mainBreakPoint === item.id && <SC.MainBreakPointLogo type="Asterisk" size={8} />}
                            </SC.IconWrapper>
                        </Tooltip>
                    )
                })}
                <Divider direction="vertical" color="var(--color-gray-200)" my={6} />
                <PreviewPopover
                    value={breakPointId}
                    list={breakPointList}
                    hoverColor={hoverColor}
                    onCreate={handleOpenCreate}
                    onDelete={handleDelete}
                    onUpdate={handleOpenUpdate}
                    onChange={handleChange}
                />
            </SC.Wrapper>
            {open && (
                <BreakPointForm
                    defaultValues={data}
                    loading={createLoading || updateLoading}
                    open={open}
                    onCreate={handleCreate}
                    onUpdate={handleUpdate}
                    onClose={handleClose}
                />
            )}
        </SC.PreviewContainer>
    )
}
